import { RestError } from '@azure/core-http';
import { useState } from 'react';
import { KnownDeliveryContainerDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import {
  useMutationEnableDisableLocker,
  useMutationEnableDisableSpace,
  useQueryGetLocker,
  useQueryGetSpace
} from '../../../lockers.queries';
import { useQueryListDeliveryContainerContents } from '../../../hubs.queries';
import { useQueryGetHub } from '../../../hubs.queries';

export interface EnableDisableLockerActionProps {
  lockerID: string;
  containerType?: KnownDeliveryContainerDtoType;
  projectID: string;
  onAbort(): void;
  onSuccess(): void;
}

export interface EnableDisableLockerActionState {
  error?: RestError;
  loading: boolean;
}
export interface EnableDisableLockerActionHandlers {
  onCancelIntent(): void;
  onSendRequest(noteContent: string, isPrivate: boolean): void;
  onDone(): void;
}

const initialState: EnableDisableLockerActionState = {
  loading: false
};

export function useEnableDisableLockerAction({
  lockerID,
  containerType,
  onAbort,
  onSuccess
}: Omit<EnableDisableLockerActionProps, 'hubName'>) {
  const [state, setState] = useState<EnableDisableLockerActionState>(initialState);
  const isSpace = containerType === KnownDeliveryContainerDtoType.Space;

  const locker = useQueryGetLocker(lockerID, !isSpace);
  const space = useQueryGetSpace(lockerID, isSpace);
  const container = isSpace ? space.data : locker.data;

  const enableLockerAction = useMutationEnableDisableLocker();
  const enableSpaceAction = useMutationEnableDisableSpace();

  const hub = useQueryGetHub(container?.hub.id);
  const lockerContents = useQueryListDeliveryContainerContents(lockerID);

  const handlers: EnableDisableLockerActionHandlers = {
    onCancelIntent() {
      onAbort();
    },
    onSendRequest(noteContent: string, isPrivate: boolean) {
      if (container) {
        setState((state) => ({ ...state, loading: true }));
        const mutationArgs = {
          enable: container.status === 'active' ? false : true,
          note: { noteContent, isPrivate }
        };
        const callbacks = {
          onSuccess() {
            setState({ loading: false });
            onSuccess();
          },
          onError(error: RestError) {
            setState({ loading: false, error });
          }
        };

        if (isSpace) {
          enableSpaceAction.mutate({ spaceID: container.id, ...mutationArgs }, callbacks);
        } else {
          enableLockerAction.mutate({ lockerID: container.id, ...mutationArgs }, callbacks);
        }
      }
    },
    onDone() {
      onSuccess();
    }
  };

  return {
    state,
    handlers,
    locker: isSpace ? space : locker,
    hub,
    lockerContents
  } as const;
}
